"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/nextjs";
import Logger from "@cappelendamm/logger";
import {
  getActiveOrganizationNumber,
  isApprovalStale,
} from "@/organizations/activeOrganization";
import { syncSchoolApprovalForCurrentUser } from "@/organizations/school/activeSchool";

/**
 * Keeps the stored organization approval in sync with the organization that is
 * currently active.
 *
 * The approval claim is only trusted when it names the active organization.
 * That leaves two cases where it has to be re-resolved: users whose metadata
 * predates `approvedOrgNumber`, and users who switched organization without the
 * switcher running the sync. In both cases this resolves the approval from
 * Crystallize, stores it on the Clerk user and refreshes the session token so
 * the new claim takes effect without a manual reload.
 *
 * Refreshing the token is enough on its own — every reader of the approval
 * (`TeacherAuthorizedWrapper`, `UnapprovedSchoolInfoBox`) is a client component
 * reading `sessionClaims`, so no server render depends on it and there is no
 * need to pay for a `router.refresh()`.
 *
 * This cannot loop: the sync always stamps `approvedOrgNumber`, including when
 * the organization turns out not to be approved, which makes the staleness
 * check false on the next render.
 */
export const SyncOrganizationApproval = () => {
  const { isLoaded, isSignedIn, sessionClaims, getToken } = useAuth();
  const syncedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!isApprovalStale(sessionClaims)) return;

    const orgNumber = getActiveOrganizationNumber(sessionClaims);
    if (!orgNumber || syncedFor.current === orgNumber) return;

    syncedFor.current = orgNumber;

    void (async () => {
      try {
        await syncSchoolApprovalForCurrentUser(orgNumber);
        await getToken({ skipCache: true });
      } catch (error) {
        // Leave the stale claim in place: it is only ever used to *grant* the
        // purchase UI, so failing here keeps the user gated rather than
        // wrongly approved. The server guard decides the actual purchase.
        Logger.error(
          `Failed to sync organization approval for orgNumber: ${orgNumber}`,
          error,
        );
      }
    })();
  }, [isLoaded, isSignedIn, sessionClaims, getToken]);

  return null;
};
